import React from "react";
import FormLayout from '../FormLayout';
import TextField from './TextField';
import { Field } from 'redux-form';
import FileUpload from '../../components/FileUpload';

import './FormFieldValue.scss';

/** Class representing a PDF layout custom text field. */
class FileField extends TextField {

    constructor(properties) {
        super({
            ...(FileField.getDefaultOptions()),
            ...properties,
        });

        this.type = FormLayout.FILE;
    }

    /**
     * Get this field default options
     */
    static getDefaultOptions() {
        const properties = super.getDefaultOptions();

        properties.extensions = ['pdf', 'txt'];

        return properties;
    }

    /**
     * Return the list of options this field handles
     */
    getHandledOptions() {
        return super.getHandledOptions()
            .filter(o => o !== FormLayout.OPTION_PLACEHOLDER)
            .concat([FormLayout.OPTION_EXTENSIONS]);
    }

    /**
     * Return this field preview input
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {Bool} inPreview
     *                 - {String} lang
     *                 - {Object} setting
     *                 - {Any} value
     *                 - {Object} resources
     */
    getInput(props) {
        return (
            <Field
                component={ FileUpload }
                type="file"
                name={this.reduxFieldName(props)}
                required={this.htmlRequired(props)}
                accept={ this.extensions.map(e => '.' + e).join(',') }
                className="form-control"
            />
        );
    }

    /**
     * Render a value
     *
     * @param {Any} value
     * @param {Object} context: an object with the following keys:
     *                   - lang (mandatory)
     *                   - t (mandatory)
     *                   - format (optional)
     *
     * @return {String|JSX.Element}
     */
    renderValue(value, context) {
        if (!value)
            return '';

        const href = typeof value === 'string' ? value : '';
        if (!href)
            return super.renderValue(value, context);

        const label = context?.t
            ? context.t('common.download')
            : 'Download';
        const is_image = href.startsWith('data:image/');
        const extension = this.guess_extension(href);
        const download_name = `attachment${extension ? `.${extension}` : ''}`;

        if (is_image) {
            return (
                <a
                    href={href}
                    download={download_name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="FormFieldValue-fileLink"
                >
                    <img
                        src={href}
                        alt={label}
                        className="FormFieldValue-imagePreview"
                    />
                </a>
            );
        }

        return (
            <a
                href={href}
                download={download_name}
                target="_blank"
                rel="noopener noreferrer"
                className="FormFieldValue-fileLink"
            >
                <i className="fa fa-download" aria-hidden="true"></i>
                {label}
            </a>
        );
    }

    /**
     * Guess a file extension from a data URL or path.
     *
     * @param {string} href
     * @returns {string}
     */
    guess_extension(href) {
        const data_match = /^data:([^;,]+)/i.exec(href || '');
        if (data_match) {
            const mime = data_match[1].toLowerCase();
            const by_mime = {
                'application/pdf': 'pdf',
                'text/plain': 'txt',
                'image/jpeg': 'jpg',
                'image/png': 'png',
                'image/webp': 'webp',
                'image/gif': 'gif',
            };
            if (by_mime[mime])
                return by_mime[mime];
            const subtype = mime.split('/')[1];
            if (subtype)
                return subtype.replace(/[^a-z0-9]+/gi, '');
        }

        const path_match = /\.([a-z0-9]+)(?:\?|$)/i.exec(href || '');
        return path_match ? path_match[1].toLowerCase() : '';
    }

    /**
     * Return the icon to use on the editor button
     */
    getIcon() {
        return 'upload';
    }
}

export default FileField;
